import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Share2, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { api, resolveImg, money, sizeOptions } from '@/lib/api';
import { ShareModal } from '@/components/ShareModal';

export function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(product.liked_by_me);
  const [likes, setLikes] = useState(product.likes || 0);
  const [shareOpen, setShareOpen] = useState(false);

  const toggleLike = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Sign in to save favourites'); return; }
    setLiked((v) => !v);
    setLikes((v) => (liked ? v - 1 : v + 1));
    try {
      const { data } = await api.post(`/products/${product.id}/like`);
      setLiked(data.liked); setLikes(data.likes);
    } catch { /* revert silently */ }
  };

  const soldOut = !product.is_presale && product.stock <= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      data-testid={`product-card-${product.id}`}
      className="group"
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden hover-zoom bg-surface aspect-[4/5] mb-4">
          <img src={resolveImg(product.images?.[0])} alt={product.name} className="w-full h-full object-cover" />
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.is_presale && (
              <span data-testid={`product-presale-badge-${product.id}`}
                className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 bg-[#3B2A10] text-[#F3D082] border border-[#7A571B] rounded-sm">
                Presale
              </span>
            )}
            {soldOut && <span className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 bg-black/70 text-white rounded-sm">Sold Out</span>}
            {product.featured && !product.is_presale && (
              <span className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 bg-gold/90 text-primary-foreground rounded-sm">Featured</span>
            )}
          </div>
          <div className="absolute top-3 right-3 flex flex-col gap-2">
            <button data-testid={`product-like-button-${product.id}`} onClick={toggleLike}
              className="w-9 h-9 rounded-full glass border border-border flex items-center justify-center hover:border-gold transition-colors">
              <Heart size={15} className={liked ? 'fill-gold text-gold' : 'text-foreground'} />
            </button>
            <button data-testid={`product-share-button-${product.id}`} onClick={(e) => { e.preventDefault(); setShareOpen(true); }}
              className="w-9 h-9 rounded-full glass border border-border flex items-center justify-center hover:border-gold transition-colors">
              <Share2 size={14} className="text-foreground" />
            </button>
          </div>
          {!soldOut && (
            <button
              data-testid={`quick-add-${product.id}`}
              onClick={(e) => { e.preventDefault(); if (sizeOptions(product.category)) navigate(`/product/${product.id}`); else add(product); }}
              className="absolute bottom-0 left-0 right-0 bg-gold text-primary-foreground py-3 text-xs font-mono uppercase tracking-widest translate-y-full group-hover:translate-y-0 transition-transform duration-300 flex items-center justify-center gap-2"
            >
              <Plus size={14} /> {sizeOptions(product.category) ? 'Choose Size' : (product.is_presale ? 'Reserve' : 'Add to Bag')}
            </button>
          )}
        </div>
      </Link>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">{product.category}</p>
          <Link to={`/product/${product.id}`} className="font-serif text-lg leading-tight hover:text-gold transition-colors">{product.name}</Link>
        </div>
        <p className="font-mono text-sm text-gold whitespace-nowrap">{money(product.price, product.currency)}</p>
      </div>
      <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
        <Heart size={11} className="text-gold" /> {likes}
      </div>
      <ShareModal open={shareOpen} onOpenChange={setShareOpen} product={product} />
    </motion.div>
  );
}
